import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Loader, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { teamAPI } from '../services';
import TeamCard from './TeamCard';
import ConfirmModal from '../../components/ConfirmModal';
import { btn } from '../../utils/buttonStyles';

const TeamList = ({
  ownerType = null,
  ownerId = null,
  createdBy = null,
  showCreateButton = true,
  showHeader = true,
  showSearch = true,
  title = 'Teams',
  onTeamClick = null,
  onCreateClick = null,
  onEditClick = null,
  emptyMessage = 'No teams yet',
  emptyDescription = 'Create your first team or join an existing one',
  createButtonText = 'Create Team',
  className = '',
  baseUrl = '/profile/teams'  // Base URL for team routes
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, teamId: null, teamName: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let teamsData;

      if (ownerType && ownerId) {
        // Fetch teams by owner (group or user)
        const response = await teamAPI.getByOwner(ownerType, ownerId);
        teamsData = response?.data || response || [];
      } else if (createdBy) {
        // Fetch teams created by a specific user
        const response = await teamAPI.getMyTeams(createdBy);
        teamsData = response?.data || response || [];
      } else {
        // Fetch all teams
        const response = await teamAPI.getAll();
        teamsData = response?.data || response || [];
      }

      setTeams(teamsData);
    } catch (err) {
      console.error('Failed to load teams:', err);
      setError(err.message || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, [ownerType, ownerId, createdBy]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleCreateTeam = () => {
    // Use custom handler if provided
    if (onCreateClick) {
      onCreateClick();
      return;
    }

    // Default behavior: navigate to create page
    const createUrl = `${baseUrl}/new`;
    if (ownerType && ownerId) {
      navigate(`${createUrl}?ownerType=${ownerType}&ownerId=${ownerId}`);
    } else {
      navigate(createUrl);
    }
  };

  const handleTeamClick = (team) => {
    if (onTeamClick) {
      onTeamClick(team);
    } else {
      // Navigate to team view page
      navigate(`${baseUrl}/${team.id || team.teamId}`);
    }
  };

  const handleDeleteTeam = (teamId, teamName) => {
    setConfirmModal({ isOpen: true, teamId, teamName });
  };

  const confirmDeleteTeam = async () => {
    if (!confirmModal.teamId) return;

    setDeleteLoading(true);
    try {
      await teamAPI.delete(confirmModal.teamId);
      fetchTeams();
      toast.success('Team deleted successfully');
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error('Failed to delete team');
    } finally {
      setDeleteLoading(false);
      setConfirmModal({ isOpen: false, teamId: null, teamName: '' });
    }
  };

  // Filter teams by search term
  const filteredTeams = teams.filter(team => {
    const teamName = team.team_name || team.teamName || team.name || '';
    const description = team.description || '';
    const searchLower = searchTerm.toLowerCase();
    return teamName.toLowerCase().includes(searchLower) ||
           description.toLowerCase().includes(searchLower);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className={`w-8 h-8 ${theme.textSecondary} animate-spin`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={fetchTeams}
          className={`${btn.base} ${btn.md} ${theme.buttonPrimary}`}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Header with Create Button */}
      {showHeader && (
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${theme.text}`}>{title}</h3>
          {showCreateButton && (
            <button
              onClick={handleCreateTeam}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-2`}
            >
              <Plus className="w-4 h-4" />
              <span>{createButtonText}</span>
            </button>
          )}
        </div>
      )}

      {/* Search */}
      {showSearch && teams.length > 0 && (
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search teams..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 border ${theme.border} rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.input}`}
            />
          </div>
        </div>
      )}

      {/* Teams Grid */}
      {filteredTeams.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredTeams.map((team) => (
            <TeamCard
              key={team.id || team.teamId}
              team={team}
              onClick={handleTeamClick}
              onEdit={onEditClick}
              baseUrl={baseUrl}
              onDelete={handleDeleteTeam}
              showActions={true}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Users className={`w-16 h-16 ${theme.textMuted} mx-auto mb-4`} />
          <h4 className={`text-lg font-medium ${theme.text} mb-2`}>
            {searchTerm ? 'No teams found' : emptyMessage}
          </h4>
          <p className={`${theme.textSecondary} mb-4`}>
            {searchTerm ? 'Try adjusting your search terms' : emptyDescription}
          </p>
          {showCreateButton && !searchTerm && (
            <button
              onClick={handleCreateTeam}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary}`}
            >
              {createButtonText}
            </button>
          )}
        </div>
      )}

      {/* Delete Team Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, teamId: null, teamName: '' })}
        onConfirm={confirmDeleteTeam}
        title="Delete Team"
        message={`Are you sure you want to delete "${confirmModal.teamName}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmStyle="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default TeamList;
